import { apiRequest } from '../../http';
import { fetchAllPages, FETCH_ALL_EXPORT_OPTIONS } from '@shared/constants';
import type { Notification } from '@shared/types';

export interface ApiNotification {
  uuid: string;
  recipientRole: 'ADMIN' | 'USER';
  recipientUserId?: string | null;
  title: string;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface NotificationsPagedResponse {
  data: ApiNotification[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
}

/** Shared by both the REST list response and the `notification:new` socket push — both send the same backend shape (uuid/recipientRole), never the FE `Notification` shape (id/role) directly. */
export function mapNotification(item: ApiNotification): Notification {
  return {
    id: item.uuid,
    role: item.recipientRole,
    title: item.title,
    message: item.message,
    severity: item.severity,
    read: item.read,
    meta: item.meta ?? undefined,
    createdAt: item.createdAt,
  };
}

function listNotificationsPage(page: number, limit: number) {
  return apiRequest('get', '/v1/notifications', undefined, {
    params: { page, limit },
  }) as Promise<NotificationsPagedResponse>;
}

/** Every in-app notification for the current user — walks all server pages. */
export async function listNotifications(): Promise<Notification[]> {
  const rows = await fetchAllPages<ApiNotification>(
    (page, limit) => listNotificationsPage(page, limit),
    FETCH_ALL_EXPORT_OPTIONS
  );
  return rows.map(mapNotification);
}

export async function markNotificationRead(uuid: string): Promise<void> {
  await apiRequest('patch', `/v1/notifications/${uuid}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiRequest('patch', '/v1/notifications/read-all');
}

export async function deleteNotification(uuid: string): Promise<void> {
  await apiRequest('delete', `/v1/notifications/${uuid}`);
}
